import React, { useEffect, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { SystemUpdateOutlined } from "@mui/icons-material";
import { isNativeApp, nativePlatform } from "./index";
import { fetchUpdatePolicy, getNativeVersion, shouldBlock } from "./updateGate";
import { APP_STORE_URL, openAppStore } from "./appStore";

/**
 * Full-screen "update the app" block for a store build below the server's
 * minimum version (native/updateGate.js). Checked at launch and again every
 * time the app returns to the foreground, so a phone that updates and comes
 * back is let through without a restart. Renders nothing on the web, on
 * Android, when the server sets no floor, or when it cannot be asked.
 */
export default function NativeUpdateGate() {
  const [block, setBlock] = useState(null); // { current, minimum, storeUrl }

  useEffect(() => {
    if (!isNativeApp() || nativePlatform() !== "ios") return undefined;

    let active = true;
    let removeListener = null;

    const evaluate = async () => {
      const [current, policy] = await Promise.all([getNativeVersion(), fetchUpdatePolicy()]);
      if (!active) return;
      const minimum = policy?.minimumNativeVersion || null;
      if (shouldBlock(current, minimum)) {
        setBlock({ current, minimum, storeUrl: policy?.storeUrl || APP_STORE_URL });
      } else {
        setBlock(null);
      }
    };

    evaluate();

    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const handle = await App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) evaluate();
        });
        if (!active) handle.remove();
        else removeListener = () => handle.remove();
      } catch {
        /* a shell without the App plugin: the launch check still ran */
      }
    })();

    return () => {
      active = false;
      if (removeListener) removeListener();
    };
  }, []);

  if (!block) return null;

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-labelledby="native-update-title"
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 3000,
        bgcolor: "#1a2a27",
        color: "#eaf2ef",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 3,
        pt: "env(safe-area-inset-top)",
        pb: "env(safe-area-inset-bottom)",
      }}
    >
      <Stack spacing={2.5} alignItems="center" sx={{ maxWidth: 340, textAlign: "center" }}>
        <SystemUpdateOutlined sx={{ fontSize: 48, color: "#7fb09a" }} />
        <Typography id="native-update-title" variant="h6" sx={{ fontWeight: 700 }}>
          Update Phieg to continue
        </Typography>
        <Typography variant="body2" sx={{ color: "#a8bcb6" }}>
          This version of the app ({block.current}) is no longer supported.
          Update it from the App Store to keep receiving your leads.
        </Typography>
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={() => openAppStore(block.storeUrl)}
          sx={{
            bgcolor: "#7fb09a",
            color: "#0b1412",
            fontWeight: 700,
            "&:hover": { bgcolor: "#6f9e89" },
          }}
        >
          Open the App Store
        </Button>
        <Typography variant="caption" sx={{ color: "#a8bcb6" }}>
          After updating, open Phieg again.
        </Typography>
      </Stack>
    </Box>
  );
}
